import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { EReducers, IReducers } from 'app/store/reducers';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';
import { InfoTipsComponent } from 'app/dialogs/info-tips/info-tips.component';
import { LeftMenuService } from 'app/store/services/left-menu.service';
import {
  changePage,
  deleteCampaign,
  loadCampaigns,
  refreshCampaigns,
  restoreCampaign,
  updateCampaign,
} from 'app/store/actions/campaigns.action';
import {
  ECampaignPaginationType,
  ECampaignStateStatus,
  ECampaignType,
  ICampaign,
  ICampaignPagination,
  ICampaignState,
} from 'app/store/models/campaigns.model';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { SessionService } from 'app/store/services/session.service';
import { IUserSessionData, UserType } from 'app/store/models/user.model';
import { PaginatorComponent } from '../../shared/ui/paginator/paginator.component';
import * as moment from 'moment';
import { NextServiceDateDialogComponent } from './next-service-date-dialog/next-service-date-dialog.component';
import { MobileService } from '../../store/services/mobile.service';
import { EMenuButtonItemType, IMenuButtonProperties } from '../../store/models/menu-button.model';
import { MatTabGroup } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { CampaignService } from '../../store/services/campaign.service';
import { ButtonComponent } from '../../shared/ui/button/button.component';

enum ECampaignTabs {
  ACTIVE,
  PENDING,
  DELETED
}

const TABS_TO_STATES = {
  [ECampaignTabs.ACTIVE]: ECampaignPaginationType.ACTIVE,
  [ECampaignTabs.PENDING]: ECampaignPaginationType.PENDING,
  [ECampaignTabs.DELETED]: ECampaignPaginationType.DELETED,
};

interface IPullMoreByCampaign {
  [key: string]: boolean;
}

interface ICurrentPage {
  [key: string]: number;
}

@Component({
  selector: 'app-list-campaigns',
  templateUrl: './list-campaigns.component.html',
  styleUrls: ['./list-campaigns.component.scss'],
})
export class ListCampaignsComponent
  implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('paginatorActive') paginatorActive: PaginatorComponent;
  @ViewChild('paginatorPending') paginatorPending: PaginatorComponent;
  @ViewChild('paginatorDeleted') paginatorDeleted: PaginatorComponent;

  @ViewChild('tabGroup') tabGroup: MatTabGroup;
  @ViewChild('lazyLoadingBorder') lazyLoadingBorder: ElementRef;
  activeCampaigns: ICampaign[];
  pendingCampaigns: ICampaign[];
  deletedCampaigns: ICampaign[];
  activeCampaignsPagination: ICampaignPagination;
  pendingCampaignsPagination: ICampaignPagination;
  deletedCampaignsPagination: ICampaignPagination;
  sessionData: IUserSessionData;
  completeCampaignButton: Button = {
    color: ButtonColor.GREEN,
    text: 'Complete Campaign',
    size: ButtonSize.SMALL,
    onClick: item => {},
  };
  activateCampaignButton: Button = {
    color: ButtonColor.BLUE,
    text: 'Activate Campaign',
    size: ButtonSize.SMALL,
    onClick: item => {
      this.store.dispatch(
        updateCampaign({
          campaign: {
            campaign_id: item.campaign_id,
            active_state: true,
            published_state: true,
          },
        }),
      );
    },
  };
  deleteCampaignButton: Button = {
    color: ButtonColor.RED,
    text: 'Delete Campaign',
    size: ButtonSize.SMALL,
    onClick: (item: ICampaign) =>
    {
      this.store.dispatch(
        deleteCampaign({
          campaign: item,
        }),
      )
    },
  };
  restoreCampaignButton: Button = {
    color: ButtonColor.GREEN,
    text: 'Restore Campaign',
    size: ButtonSize.SMALL,
    onClick: (item: ICampaign) =>
      this.store.dispatch(
        restoreCampaign({
          campaign: item,
        }),
      ),
  };
  editCampaignButton: Button = {
    color: ButtonColor.ORANGE,
    text: 'Edit Campaign',
    size: ButtonSize.SMALL,
  };
  pendingCampaignButton: Button = {
    color: ButtonColor.BLUE,
    text: 'Make Pending',
    size: ButtonSize.SMALL,
    onClick: item => {
      this.store.dispatch(
        updateCampaign({
          campaign: {
            campaign_id: item.campaign_id,
            active_state: false,
          },
        }),
      );
    },
  };
  pendingCampaignsMenuButtonProperties: IMenuButtonProperties = {
    items: [
      {
        label: null,
        type: EMenuButtonItemType.BUTTON,
        class: 'alert-success',
        onClick: (item: ICampaign) => {
          item = JSON.parse(JSON.stringify(item));
          
          // Check if button should be disabled
          if (item.campaign_recruit && !this.sessionData.c2recruit) {
            this.store.dispatch(openSnackbar({
              snackbarData: {
                data: {
                  message: 'This campaign is of type \'click2recruit\'. Your click2recruit feature is currently disabled. If you want to activate this campaign, please contact the customer support.',
                  type: 'warning',
                },
              },
            }));
            return;
          }
          
          if (item.campaign_published_state) {
            this.campaignService.updateCampaign({
              campaign_id: item.campaign_id,
              active_state: true,
            }).subscribe(() => this.mobileUpdateAllTabs());
            return;
          }

          const route = this.getCompleteButtonRouter(item);

          this.router.navigate(route, {
            queryParams: this.getCompleteButtonQueryParams(),
          });
        },
        getLabel: (target: ICampaign) => {
          target = JSON.parse(JSON.stringify(target));
          return target.campaign_published_state ? 'Activate' : 'Complete';
        },
        isShown: (target: ICampaign) => {
          return this.sessionData.user_type !== UserType.PARTNER;
        },
        getDisabled: (target: ICampaign) => {
          // Disable if campaign is recruit but user doesn't have c2recruit enabled
          return target.campaign_recruit && !this.sessionData.c2recruit;
        },
      },
      {
        label: 'Delete',
        type: EMenuButtonItemType.BUTTON,
        class: 'alert-danger',
        onClick: (t) => this.campaignService.deleteCampaign(t).subscribe(() => this.mobileUpdateAllTabs()),
        isShown: () => this.sessionData.user_type !== UserType.PARTNER,
      },
    ],
  };
  restoreCampaignsButtonProperties: IMenuButtonProperties = {
    items: [
      {
        label: 'Restore',
        type: EMenuButtonItemType.BUTTON,
        onClick: (target: ICampaign) => {
          this.campaignService.restoreCampaign(target)
            .subscribe({
              next: () => this.mobileUpdateAllTabs(),
              error: (err) => {
                // Check if error is about recruit campaign validation
                if (err?.error?.code === 1005 || err?.error?.message?.includes('C2Recruit') || err?.error?.message?.includes('recruit campaign')) {
                  this.store.dispatch(openSnackbar({
                    snackbarData: {
                      data: {
                        message: err?.error?.message || 'Cannot restore recruit campaign. C2Recruit is not enabled for this account.',
                        type: 'warning',
                      },
                    },
                  }));
                } else {
                  this.store.dispatch(openSnackbar({
                    snackbarData: { data: { message: err?.error?.message?.[0] || 'Failed to restore campaign', type: 'error' } },
                  }));
                }
              },
            });
        },
        isShown: (target: ICampaign) => {
          return this.sessionData.user_type !== UserType.PARTNER;
        },
        getDisabled: (target: ICampaign) => {
          // Disable if campaign is recruit but user doesn't have c2recruit enabled
          return target.campaign_recruit && !this.sessionData.c2recruit;
        },
      },
    ],
  };
  isMobileView: boolean;
  activeCampaignMenuButtonItems: IMenuButtonProperties = {
    items: [
      {
        label: 'Edit',
        type: EMenuButtonItemType.LINK,
        isShown: () => this.sessionData.permissions.can_update_campaign,
        getRoute: (item: ICampaign) => {
          return ['/campaigns', item.campaign_id, 'edit'];
        },
      },
      {
        label: 'Make Pending',
        type: EMenuButtonItemType.BUTTON,
        isShown: () => this.sessionData.permissions.can_update_campaign,
        onClick: (item: ICampaign) => {
          this.campaignService.updateCampaign({
            campaign_id: item.campaign_id,
            active_state: false,
          }).subscribe(() => this.mobileUpdateAllTabs());
        },
      },
    ],
  };
  userTypes = UserType;
  protected readonly Object = Object;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private popupShown: boolean;
  private lazyLoadNextPageInProgress: boolean;
  private activePull: ECampaignPaginationType;
  private canPullMoreData: IPullMoreByCampaign = {
    [ECampaignPaginationType.ACTIVE]: true,
    [ECampaignPaginationType.PENDING]: true,
    [ECampaignPaginationType.DELETED]: true,
  };
  private currentPage: ICurrentPage = {
    [ECampaignPaginationType.ACTIVE]: 0,
    [ECampaignPaginationType.PENDING]: 0,
    [ECampaignPaginationType.DELETED]: 0,
  };
  private markForMobileRefresh: boolean;

  constructor(
    private store: Store<IReducers>,
    private leftMenuService: LeftMenuService,
    private sessionService: SessionService,
    private readonly mobileService: MobileService,
    private readonly router: Router,
    private readonly campaignService: CampaignService,
  ) {
    this.mobileService.observe()
      .pipe(takeUntil(this.destroy$))
      .subscribe(isMobile => this.isMobileView = isMobile);
  }

  @HostListener('window:scroll', ['$event']) // for window scroll events
  onScroll() {
    if (!this.isMobileView || !this.lazyLoadingBorder) {
      return;
    }

    const lazyBorderRect = this.lazyLoadingBorder.nativeElement.getBoundingClientRect();

    if (
      !this.lazyLoadNextPageInProgress &&
      lazyBorderRect.top <= window.innerHeight &&
      this.canPullMoreData[TABS_TO_STATES[this.tabGroup.selectedIndex]]
    ) {
      this.mobileLoadNextPage();
    }
  }

  ngAfterViewInit() {
    if (this.isMobileView) {
      this.mobileLoadNextPage(ECampaignPaginationType.ACTIVE);
      this.mobileLoadNextPage(ECampaignPaginationType.PENDING);
      this.mobileLoadNextPage(ECampaignPaginationType.DELETED);
    }
    if (this.paginatorActive) {
      this.paginatorActive.change
        .pipe(
          takeUntil(this.destroy$),
          tap(paginator => {
            this.store.dispatch(
              changePage({
                page: paginator.page,
                campaign_type: ECampaignPaginationType.ACTIVE,
                page_size: paginator.pageSize,
              }),
            );
          }),
        )
        .subscribe();
    }

    if (this.paginatorPending) {
      this.paginatorPending.change
        .pipe(
          takeUntil(this.destroy$),
          tap(paginator => {
            this.store.dispatch(
              changePage({
                page: paginator.page,
                campaign_type: ECampaignPaginationType.PENDING,
                page_size: paginator.pageSize,
              }),
            );
          }),
        )
        .subscribe();
    }

    if (this.paginatorDeleted) {
      this.paginatorDeleted.change
        .pipe(
          takeUntil(this.destroy$),
          tap(paginator => {
            this.store.dispatch(
              changePage({
                page: paginator.page,
                campaign_type: ECampaignPaginationType.DELETED,
                page_size: paginator.pageSize,
              }),
            );
          }),
        )
        .subscribe();
    }
  }

  mobileUpdateAllTabs() {
    const allTabs = Object.values(TABS_TO_STATES);
    this.activeCampaigns = [];
    this.pendingCampaigns = [];
    this.deletedCampaigns = [];


    for (const tab of allTabs) {
      const currentPage = this.currentPage[tab];

      let action: string;
      switch (tab) {
        case ECampaignPaginationType.ACTIVE:
          action = 'Active';
          break;
        case ECampaignPaginationType.PENDING:
          action = 'Pending';
          break;
        case ECampaignPaginationType.DELETED:
          action = 'Deleted';
          break;
      }

      if (!action) {
        continue;
      }
      for (let i = 1; i <= currentPage; i++) {
        this.campaignService['get' + action + 'Campaigns']({
          current_page: i,
          page_size: 10,
        }).subscribe(response => {
          if (!this[action.charAt(0).toLowerCase() + action.slice(1) + 'Campaigns']) {
            this[action.charAt(0).toLowerCase() + action.slice(1) + 'Campaigns'] = [];
          }
          this[action.charAt(0).toLowerCase() + action.slice(1) + 'Campaigns'] = [
            ...this[action.charAt(0).toLowerCase() + action.slice(1) + 'Campaigns'],
            ...response.items,
          ];
          this.canPullMoreData[tab] = response.has_more_data;
        });
      }
    }
  }

  mobileLoadNextPage(tab?: ECampaignPaginationType) {
    if (!tab) {
      tab = TABS_TO_STATES[this.tabGroup.selectedIndex];
    }
    const currentPage = this.currentPage[tab]++;
    let action: string;
    switch (tab) {
      case ECampaignPaginationType.ACTIVE:
        action = 'Active';
        break;
      case ECampaignPaginationType.PENDING:
        action = 'Pending';
        break;
      case ECampaignPaginationType.DELETED:
        action = 'Deleted';
        break;
    }

    if (!action) {
      return;
    }

    this.campaignService['get' + action + 'Campaigns']({
      current_page: currentPage,
      page_size: 10,
    }).subscribe(response => {
      if (!this[action.charAt(0).toLowerCase() + action.slice(1) + 'Campaigns']) {
        this[action.charAt(0).toLowerCase() + action.slice(1) + 'Campaigns'] = [];
      }
      this[action.charAt(0).toLowerCase() + action.slice(1) + 'Campaigns'] = [
        ...this[action.charAt(0).toLowerCase() + action.slice(1) + 'Campaigns'],
        ...response.items,
      ];
      this.canPullMoreData[tab] = response.has_more_data;
    });
  }

  ngOnInit() {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe((sessionData: IUserSessionData) => {
        this.store.dispatch(
          loadCampaigns({
            pagination: {
              current_page: 1,
              page_size: 15,
            },
          }),
        );
        if (sessionData.popup_tips) {
          if (this.popupShown) {
            return;
          }
          this.popupShown = true;
          this.onInfoImageClick();
        }
        this.store
          .select(EReducers.CAMPAIGNS)
          .pipe(takeUntil(this.destroy$))
          .subscribe((state: ICampaignState) => {
            if (this.isMobileView) {
              return;
            }
            if (state.status === ECampaignStateStatus.UPDATED) {
              this.activePull = undefined;
              this.store.dispatch(
                refreshCampaigns({
                  pagination: state.pagination,
                }),
              );
            }

            if (state.status === ECampaignStateStatus.ERROR && state.error) {
              // Handle errors from restore campaign or update campaign
              const error = state.error;
              if (error?.code === 1005 || error?.message?.includes('C2Recruit') || error?.message?.includes('recruit campaign')) {
                this.store.dispatch(openSnackbar({
                  snackbarData: {
                    data: {
                      message: error?.message || 'Cannot restore/activate recruit campaign. C2Recruit is not enabled for this account.',
                      type: 'warning',
                    },
                  },
                }));
              } else {
                this.store.dispatch(openSnackbar({
                  snackbarData: { data: { message: error?.message?.[0] || error?.message || 'An error occurred', type: 'error' } },
                }));
              }
            }

            if (state.active_campaigns) {
              this.activeCampaigns = state.active_campaigns;
            }

            if (state.pending_campaigns) {
              this.pendingCampaigns = state.pending_campaigns;
            }

            if (state.deleted_campaigns) {
              this.deletedCampaigns = state.deleted_campaigns;
            }

            if (state.pagination) {
              if (state.pagination[ECampaignPaginationType.ACTIVE]) {
                this.activeCampaignsPagination =
                  state.pagination[ECampaignPaginationType.ACTIVE];
              }
              if (state.pagination[ECampaignPaginationType.PENDING]) {
                this.pendingCampaignsPagination =
                  state.pagination[ECampaignPaginationType.PENDING];
              }
              if (state.pagination[ECampaignPaginationType.DELETED]) {
                this.deletedCampaignsPagination =
                  state.pagination[ECampaignPaginationType.DELETED];
              }
            }
          });
        this.sessionData = sessionData;
      });
    this.leftMenuService.expand('campaigns');
  }

  ngOnDestroy() {
    this.destroy$.next(false);
  }

  onInfoImageClick() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          content: InfoTipsComponent,
          title: 'Tips',
          canClose: true,
          dialogMinWidth: '700px',
          componentData: {
            type: 'campaign',
          },
        },
      }),
    );
  }

  getCampaignType(campaign: ICampaign) {
    let campaignType =
      campaign.campaign_type.charAt(0).toUpperCase() +
      campaign.campaign_type.substr(1);

    if (campaign.campaign_type === ECampaignType.FREQUENCY) {
      campaignType += ` ${campaign.campaign_frequency} Months`;
    }

    return campaignType;
  }

  getEditButtonRoute(campaign: ICampaign) {
    return ['/campaigns', campaign.campaign_id, 'edit'];
  }

  getCompleteButtonRouter(campaign: ICampaign) {
    if (campaign.campaign_progress === 1) {
      return ['/campaigns', campaign.campaign_id, 'communication-content'];
    } else if (campaign.campaign_progress === 2) {
      return ['/campaigns', campaign.campaign_id, 'preview-content'];
    }

    return ['/campaigns', campaign.campaign_id, 'communication-content'];
  }

  getCompleteButtonQueryParams() {
    return {
      edit: true,
    };
  }

  getScheduledDate(campaign: ICampaign) {
    if (!campaign.scheduled_date || campaign.scheduled_date.length === 0) {
      if (campaign.campaign_active === true && campaign.campaign_published_state === false) {
        return 'No email scheduled';
      }
      return 'Scheduled';
    }

    if (campaign.scheduled_emails > 2000) {
      const emails = 2000 - this.sessionData.emails_perday;

      return `${emails} on ${moment(campaign.scheduled_date).format(
        'MM/DD/YYYY',
      )}`;
    }


    return `${campaign.scheduled_emails} on ${moment(
      campaign.scheduled_date,
    ).format('MM/DD/YYYY')}`;
  }

  openNextServiceDateDialog(item: ICampaign) {
    this.store.dispatch(openDialog({
      dialogData: {
        content: NextServiceDateDialogComponent,
        title: `Campaign ${item.campaign_name}`,
        dialogWidth: '1280px',
        dialogMinHeight: '600px',
        componentData: {
          item,
        },
        canClose: true,
        buttons: [],
      },
    }));
  }

  isActivateButtonDisabled(item: ICampaign): boolean {
    return !!(item.campaign_recruit && !this.sessionData?.c2recruit);
  }
}
